import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Moon, Search, Sun } from "lucide-react";
import { useMemo, useState } from "react";
import { fetchArticles, fetchCategories, fetchSiteSettings } from "@/services/news";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BreakingNewsBar } from "@/components/layout/BreakingNewsBar";
import { SocialNetworkIcon } from "@/lib/socialIcons";
import { useUiStore } from "@/stores/ui";
import { cn } from "@/lib/utils";
import { isWithin48h, selectFreshFeed } from "@/lib/freshness";

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const { theme, toggleTheme } = useUiStore();

  const { data: site } = useQuery({
    queryKey: ["site"],
    queryFn: fetchSiteSettings,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const { data: breakingData } = useQuery({
    queryKey: ["articles", "breaking-header"],
    queryFn: () => fetchArticles({ breaking: "true", ordering: "-publish_at" }),
    staleTime: 60_000,
  });

  const { data: latestData } = useQuery({
    queryKey: ["articles", "plantao-ticker"],
    queryFn: () => fetchArticles({ ordering: "-publish_at" }),
    staleTime: 60_000,
  });

  const tickerItems = useMemo(() => {
    const breaking = breakingData?.results || [];
    const latest = latestData?.results || [];
    const fresh = selectFreshFeed([breaking, latest], 8);
    const onlyFresh = fresh.filter((article) => isWithin48h(article));
    if (onlyFresh.length) return onlyFresh;
    if (breaking.length) return breaking.slice(0, 8);
    return fresh;
  }, [breakingData?.results, latestData?.results]);

  const navCategories = categories.filter((category) => (category.article_count || 0) > 0);

  function onSearch(event: React.FormEvent) {
    event.preventDefault();
    const q = query.trim();
    if (q) navigate(`/busca?q=${encodeURIComponent(q)}`);
  }

  const today = new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
  }).format(new Date());

  return (
    <header className="sticky top-0 z-40 border-b border-brand-outline-variant/50 bg-brand-surface-lowest shadow-sm">
      <BreakingNewsBar items={tickerItems} />

      <div className="portal-container py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/"
            className="flex items-center gap-2.5 font-display text-2xl font-bold tracking-tight text-brand-primary"
          >
            <img
              src="/logo-eusebio-news-44.png"
              srcSet="/logo-eusebio-news-44.png 1x, /logo-eusebio-news-88.png 2x"
              alt=""
              width={44}
              height={44}
              className="h-11 w-11 shrink-0 object-contain"
              decoding="async"
              fetchPriority="low"
            />
            <span>{site?.site_name || "Eusebio News"}</span>
          </Link>

          <form onSubmit={onSearch} className="order-3 w-full max-w-xl lg:order-none lg:flex-1 lg:px-8">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-outline" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar noticias..."
                className="rounded-full border-brand-outline-variant/60 bg-brand-surface pl-10 focus-visible:ring-brand-primary"
                aria-label="Buscar noticias"
              />
            </div>
          </form>

          <div className="flex items-center gap-2 text-sm text-brand-on-surface-variant sm:gap-3">
            <span className="hidden items-center gap-1 sm:inline-flex">
              <Calendar className="h-4 w-4 text-brand-primary" />
              {today}
            </span>
            {(site?.social_links?.length ? site.social_links : []).map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={link.label}
                title={link.label}
                className="inline-flex h-9 w-9 items-center justify-center rounded text-brand-primary transition hover:bg-brand-surface-container"
              >
                <SocialNetworkIcon icon={link.icon} className="h-[18px] w-[18px]" />
              </a>
            ))}
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={toggleTheme}
              aria-label="Alternar tema"
              className="rounded text-brand-ink"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <nav className="mt-4 flex gap-1 overflow-x-auto border-t border-brand-outline-variant/40 pt-3" aria-label="Categorias">
          <Link
            to="/"
            className={cn(
              "whitespace-nowrap rounded px-3 py-2 text-sm font-semibold transition",
              location.pathname === "/"
                ? "bg-brand-chrome text-white"
                : "text-brand-ink hover:bg-brand-surface-container",
            )}
          >
            Home
          </Link>
          {navCategories.map((category) => {
            const active = location.pathname === `/categoria/${category.slug}`;
            return (
              <Link
                key={category.id}
                to={`/categoria/${category.slug}`}
                className={cn(
                  "whitespace-nowrap rounded px-3 py-2 text-sm font-semibold transition",
                  active
                    ? "bg-brand-chrome text-white"
                    : "text-brand-ink hover:bg-brand-surface-container",
                )}
              >
                {category.name}
              </Link>
            );
          })}
        </nav>
      </div>
    </header>
  );
}
